import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cssMap } from '@compiled/react';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { SourcesSidePanelCard } from '@atlassian/lumina/sources-side-panel-card';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onAIAnswerDialogClick } from '../../../ai-answer-dialog/analytics';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { useKnowledgeSmartCardAnalytics } from '../knowledge-smart-card/hooks/use-knowledge-smart-card-analytics';
import { SourcesStack } from '../sources-stack/sources-stack';

const messages = defineMessages({
	sources_button_text: {
		id: 'search_ai.sources_side_panel_button_text',
		defaultMessage: '{numSources, plural, one {Source} other {Sources}}',
		description: 'Sources button text that opens side panel',
	},
	sources_panel_title: {
		id: 'search_ai.sources_side_panel_title',
		defaultMessage:
			'{numSources, plural, one {{numSources, number} Source} other {{numSources, number} Sources}}',
		description: 'Title shown in the sources side panel',
	},
	close_panel: {
		id: 'search_ai.sources_side_panel_close',
		defaultMessage: 'Close sources panel',
		description: 'Accessible label for close button on sources side panel',
	},
});

const styles = cssMap({
	panel: {
		position: 'fixed',
		top: token('space.600'),
		right: token('space.0'),
		bottom: token('space.0'),
		width: '419px',
		backgroundColor: token('elevation.surface.overlay'),
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderLeftColor: token('color.border'),
		display: 'flex',
		flexDirection: 'column',
	},
	panelHeader: {
		justifyContent: 'space-between',
		alignItems: 'center',
		height: '77px',
		flexShrink: 0,
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
	panelContent: {
		gap: token('space.150'),
		overflowY: 'auto',
		flexGrow: 1,
		paddingTop: token('space.100'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	sourceItem: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
});

export const SourcesSidePanel = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const context = useAIAnswerContext();
	const { commonAttributes } = context;
	const { sources } = commonAttributes;
	const { handleSmartCardClick, searchSessionId } = useKnowledgeSmartCardAnalytics({
		aiAnswerDialogContext: context,
	});
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const panelRef = useRef<HTMLDivElement>(null);

	const { mounted: isChatMounted } = useUIStore();
	const prevChatMounted = useRef(isChatMounted);

	useEffect(() => {
		if (isChatMounted && !prevChatMounted.current) {
			setIsOpen(false);
		}
		prevChatMounted.current = isChatMounted;
	}, [isChatMounted]);

	useEffect(() => {
		if (isOpen && panelRef.current) {
			panelRef.current.focus();
		}
	}, [isOpen]);

	const publish = usePublish('ai-mate');

	const handleToggle = useCallback(() => {
		setIsOpen((prev) => {
			if (!prev) {
				fireAnalyticsEvent(
					onAIAnswerDialogClick({
						...commonAttributes,
						elementInteracted: 'sources-side-panel-button',
					}),
				);
				fireSAINUsageEvent({ metCriteria: SainAction.VIEW_ALL_SOURCES, commonAttributes });
				if (isChatMounted) {
					publish({ type: 'chat-close', source: 'sain', data: {} });
				}
			}
			return !prev;
		});
	}, [isChatMounted, publish, fireAnalyticsEvent, fireSAINUsageEvent, commonAttributes]);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	if (!sources || sources.length === 0) {
		return null;
	}

	const numSources = sources.length;

	return (
		<>
			<Button onClick={handleToggle} testId="sources-side-panel-button">
				<Inline space="space.050" alignBlock="center">
					<SourcesStack sources={sources} />
					{formatMessage(messages.sources_button_text, { numSources })}
				</Inline>
			</Button>
			{isOpen &&
				createPortal(
					<Box xcss={styles.panel} testId="sources-side-panel" ref={panelRef} tabIndex={-1}>
						<Inline xcss={styles.panelHeader}>
							<Heading as="h2" size="xsmall">
								{formatMessage(messages.sources_panel_title, { numSources })}
							</Heading>
							<IconButton
								appearance="subtle"
								icon={CrossIcon}
								label={formatMessage(messages.close_panel)}
								onClick={handleClose}
							/>
						</Inline>
						<Stack xcss={styles.panelContent}>
							{sources.map((source, index) => (
								<Box
									key={source.url || index}
									xcss={styles.sourceItem}
									testId={`sources-side-panel-item-${index}`}
								>
									<SourcesSidePanelCard
										source={source}
										index={index}
										searchSessionId={searchSessionId}
										onClickHandler={handleSmartCardClick}
									/>
								</Box>
							))}
						</Stack>
					</Box>,
					document.body,
				)}
		</>
	);
};
